import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { isValidJalaaliDate, toGregorian, toJalaali } from 'jalaali-js';
import { formatDate, formatDateTime } from '@/core/utils/date';

const months = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

const monthLength = (year: number, month: number): number => {
  let day = 31;
  while (day > 0 && !isValidJalaaliDate(year, month, day)) day -= 1;
  return day;
};

const jalaliParts = (value?: Dayjs): { year: number; month: number; day: number } => {
  const date = toJalaali((value ?? dayjs()).toDate());
  return { year: date.jy, month: date.jm, day: date.jd };
};

interface JalaliDateTimePickerProps {
  value?: Dayjs;
  onChange?: (value: Dayjs | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  showTime?: boolean;
}

export const JalaliDateTimePicker = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'تاریخ و ساعت سررسید را انتخاب کنید',
  showTime = true,
}: JalaliDateTimePickerProps): React.ReactElement => {
  const rootRef = useRef<HTMLDivElement>(null);
  const initial = useMemo(() => jalaliParts(value), [value]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(initial);
  const [time, setTime] = useState(value?.format('HH:mm') ?? '09:00');

  useEffect(() => {
    if (!open) return;
    setView(jalaliParts(value));
    setTime(value?.format('HH:mm') ?? '09:00');
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const closeWhenClickedAway = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeWhenClickedAway);
    return () => document.removeEventListener('mousedown', closeWhenClickedAway);
  }, [open]);

  const days = useMemo(() => {
    const gregorian = toGregorian(view.year, view.month, 1);
    const firstDay = new Date(gregorian.gy, gregorian.gm - 1, gregorian.gd);
    const offset = (firstDay.getDay() + 1) % 7;
    return { offset, count: monthLength(view.year, view.month) };
  }, [view.month, view.year]);

  const selected = value ? jalaliParts(value) : null;
  const today = jalaliParts();

  const changeMonth = (direction: -1 | 1): void => {
    setView((current) => {
      const nextMonth = current.month + direction;
      if (nextMonth < 1) return { ...current, year: current.year - 1, month: 12, day: 1 };
      if (nextMonth > 12) return { ...current, year: current.year + 1, month: 1, day: 1 };
      return { ...current, month: nextMonth, day: 1 };
    });
  };

  const selectDay = (day: number): void => {
    const gregorian = toGregorian(view.year, view.month, day);
    const [hours, minutes] = time.split(':').map(Number);
    onChange?.(dayjs(new Date(
      gregorian.gy,
      gregorian.gm - 1,
      gregorian.gd,
      showTime ? hours || 0 : 0,
      showTime ? minutes || 0 : 0,
    )));
    setOpen(false);
  };

  const updateTime = (nextTime: string): void => {
    setTime(nextTime);
    if (!value) return;
    const [hours, minutes] = nextTime.split(':').map(Number);
    onChange?.(value.hour(hours || 0).minute(minutes || 0).second(0));
  };

  return (
    <div className="relative" ref={rootRef}>
      <div className={`flex min-h-10 items-center gap-2 rounded-lg border px-3 py-1.5 transition ${disabled ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400' : 'border-slate-300 bg-white hover:border-brand-400 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100'}`}>
        <button aria-expanded={open} aria-haspopup="dialog" className="min-w-0 flex-1 truncate text-right text-sm outline-none" disabled={disabled} onClick={() => setOpen((current) => !current)} type="button">
          {value ? (showTime ? formatDateTime(value.toDate()) : formatDate(value.toDate())) : <span className="text-slate-400">{placeholder}</span>}
        </button>
        {value && !disabled ? <button aria-label={showTime ? 'پاک کردن تاریخ و ساعت' : 'پاک کردن تاریخ'} className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={() => onChange?.(undefined)} type="button">×</button> : null}
      </div>

      {open ? <div className="absolute right-0 z-50 mt-2 w-[min(21rem,calc(100vw-3rem))] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl shadow-slate-900/15" dir="rtl" role="dialog" aria-label="تقویم شمسی">
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
          <button aria-label="ماه بعد" className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" onClick={() => changeMonth(1)} type="button">‹</button>
          <strong className="text-sm text-slate-800">{months[view.month - 1]} {new Intl.NumberFormat('fa-IR').format(view.year)}</strong>
          <button aria-label="ماه قبل" className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" onClick={() => changeMonth(-1)} type="button">›</button>
        </div>

        <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-slate-400">{weekDays.map((day) => <span key={day}>{day}</span>)}</div>
        <div className="mt-2 grid grid-cols-7 gap-1">
          {Array.from({ length: days.offset }, (_, index) => <span key={`empty-${index}`} />)}
          {Array.from({ length: days.count }, (_, index) => {
            const day = index + 1;
            const isSelected = selected?.year === view.year && selected.month === view.month && selected.day === day;
            const isToday = today.year === view.year && today.month === view.month && today.day === day;
            return <button className={`grid aspect-square place-items-center rounded-lg text-xs font-bold transition ${isSelected ? 'bg-brand-600 text-white shadow-sm' : isToday ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100'}`} key={day} onClick={() => selectDay(day)} type="button">{new Intl.NumberFormat('fa-IR').format(day)}</button>;
          })}
        </div>

        <div className="mt-3 flex items-center gap-3 border-t border-slate-100 pt-3">
          {showTime ? <><span className="text-xs font-bold text-slate-500">ساعت</span><input className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-brand-500" dir="ltr" onChange={(event) => updateTime(event.target.value)} type="time" value={time} /></> : <span className="flex-1 text-xs font-bold text-slate-500">تاریخ شمسی</span>}
          <button className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600 hover:bg-slate-200" onClick={() => { const now = dayjs(); setView(jalaliParts(now)); setTime(now.format('HH:mm')); onChange?.(showTime ? now : now.startOf('day')); setOpen(false); }} type="button">{showTime ? 'اکنون' : 'امروز'}</button>
        </div>
      </div> : null}
    </div>
  );
};

export const JalaliDatePicker = (props: Omit<JalaliDateTimePickerProps, 'showTime'>): React.ReactElement => (
  <JalaliDateTimePicker {...props} showTime={false} placeholder={props.placeholder ?? 'تاریخ شمسی را انتخاب کنید'} />
);
